import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Fingerprint, LogOut, LayoutDashboard, CalendarCheck, Users, UserCog, LifeBuoy, ClipboardCheck, BadgeDollarSign, UserSquare2, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import OverviewTab from "@/components/tabs/OverviewTab";
import ErrorBoundary from "@/components/ErrorBoundary";
import PlanGate from "@/components/PlanGate";
import ExpiredGate from "@/components/ExpiredGate";
import { isExpiredUser } from "@/lib/plans";
import AttendanceTab from "@/components/tabs/AttendanceTab";
import LatenessTab from "@/components/tabs/LatenessTab";
import AccountabilityTab from "@/components/tabs/AccountabilityTab";
import DeductionsTab from "@/components/tabs/DeductionsTab";
import PerformanceTab from "@/components/tabs/PerformanceTab";
import EmployeesTab from "@/components/tabs/EmployeesTab";
import ExtensionTab from "@/components/tabs/ExtensionTab";
import SettingsTab from "@/components/tabs/SettingsTab";
import SupportTab from "@/components/tabs/SupportTab";
import AdminTab from "@/components/tabs/AdminTab";
import StaffPortalTab from "@/components/tabs/StaffPortalTab";
import StaffPortalGateTab from "@/components/tabs/StaffPortalGateTab";
import PermissionsTab from "@/components/tabs/PermissionsTab";
import AuditLogTab from "@/components/tabs/AuditLogTab";
import MySubscriptionTab from "@/components/tabs/MySubscriptionTab";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [groupTab, setGroupTab] = useState("attendance");
  const [portalTab, setPortalTab] = useState("portalgate");
  const [searchParams, setSearchParams] = useSearchParams();

  // ترحيب بعد تجديد ناجح: PaymentResult يحوّل إلى /dashboard?renewed=1
  useEffect(() => {
    if (searchParams.get("renewed") === "1") {
      toast.success("تم تجديد اشتراكك بنجاح — أهلاً بعودتك، كل الخدمات تعمل الآن",
        { duration: 6000, id: "renewed-welcome" });
      setTab("overview");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const GROUP_CHILDREN = [
    { id: "attendance", label: "سجل الدوام", testId: "tab-attendance-log" },
    { id: "lateness", label: "التقارير", testId: "tab-lateness-report" },
    { id: "accountability", label: "المسائلات", testId: "tab-accountability-report" },
    { id: "deductions", label: "قرارات الحسم", testId: "tab-deductions" },
  ];
  const GROUP_IDS = ["attendance", "lateness", "accountability", "deductions"];
  const inGroup = GROUP_IDS.includes(tab);

  const PORTAL_CHILDREN = [
    { id: "portalgate", label: "بوابة الموظفين", testId: "tab-portal-sub-gate" },
    { id: "permissions", label: "الصلاحيات", testId: "tab-permissions" },
    { id: "audit", label: "سجل التدقيق", testId: "tab-audit" },
    { id: "portal", label: "التعاميم", testId: "tab-staff-portal" },
  ];
  const PORTAL_IDS = ["portalgate", "permissions", "audit", "portal"];
  const inPortal = PORTAL_IDS.includes(tab);

  const SETTINGS_CHILDREN = [
    { id: "settings-timing", label: "توقيت الدوام", testId: "tab-settings-timing" },
    { id: "settings-inquiries", label: "ضبط المسائلات والاستيراد", testId: "tab-settings-inquiries" },
    { id: "settings-holidays", label: "الإجازات والعطل", testId: "tab-settings-holidays" },
    { id: "settings-branding", label: "الترويسة والشعار", testId: "tab-settings-branding" },
    { id: "extension", label: "إضافة أداة الكروم", testId: "tab-extension-manager" },
  ];
  const SETTINGS_IDS = ["settings-timing", "settings-inquiries", "settings-holidays", "settings-branding", "extension"];
  const inSettings = SETTINGS_IDS.includes(tab);

  const MAIN_TABS = [
    { id: "overview", label: "لوحة المؤشرات", icon: LayoutDashboard, testId: "tab-overview" },
    { id: "group", label: "متابعة الحضور والانصراف", icon: CalendarCheck, testId: "tab-attendance-group" },
    { id: "employees", label: "الموظفون", icon: Users, testId: "tab-employees" },
    { id: "portalgate", label: "بوابة الموظفين", icon: UserSquare2, testId: "tab-portal-gate" },
    { id: "performance", label: "تقييم الأداء الوظيفي", icon: ClipboardCheck, testId: "tab-performance" },
    { id: "settingsgroup", label: "الإعدادات", icon: Settings, testId: "tab-settings" },
    { id: "support", label: "الدعم", icon: LifeBuoy, testId: "tab-support" },
    { id: "subscription", label: "اشتراكي", icon: BadgeDollarSign, testId: "tab-my-subscription" },
    ...(user?.role === "admin"
      ? [{ id: "admin", label: "الاشتراكات", icon: UserCog, testId: "tab-admin" }]
      : []),
  ];

  const openMain = (id) => {
    if (id === "group") {
      // حارس: آخر قسم فرعي محفوظ قد يكون «الإعدادات» قبل نقلها خارج المجموعة
      setTab(GROUP_IDS.includes(groupTab) ? groupTab : "attendance");
    } else if (id === "portalgate") {
      setTab(PORTAL_IDS.includes(portalTab) ? portalTab : "portalgate");
    } else if (id === "settingsgroup") {
      setTab("settings-timing");
    } else {
      setTab(id);
    }
  };

  const openChild = (c) => {
    setGroupTab(c.id);
    setTab(c.id);
  };

  const openSettingsChild = (c) => {
    setTab(c.id);
  };

  const openPortalChild = (c) => {
    setPortalTab(c.id);
    setTab(c.id);
  };

  const expired = isExpiredUser(user);
  const gate = (node) => <ExpiredGate onRenew={() => setTab("subscription")}>{node}</ExpiredGate>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard">
      <header className="bg-emerald-950 text-white sticky top-0 z-20 shadow-lg">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-900 flex items-center justify-center">
              <Fingerprint className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="font-extrabold text-base sm:text-lg leading-tight">راصد حضوري</h1>
              <p className="text-[11px] text-emerald-300/70 hidden sm:block">نظام تدقيق وتحليل الحضور الذكي</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs sm:text-sm text-emerald-100 hidden md:block" data-testid="header-user-email">
              {user?.email}
            </span>
            <button onClick={logout} data-testid="logout-button"
              className="flex items-center gap-1.5 text-xs sm:text-sm bg-emerald-900 hover:bg-emerald-800 rounded-lg px-3 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {expired && (
          <div className="mb-4 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3"
            data-testid="expired-banner" role="alert">
            <span className="text-sm font-bold text-amber-900">
              انتهى اشتراكك — جدد الآن لاستعادة كل الخدمات (بياناتك محفوظة بالكامل)
            </span>
            <button onClick={() => setTab("subscription")} data-testid="expired-banner-renew"
              className="mr-auto text-xs font-bold bg-emerald-900 hover:bg-emerald-950 text-white rounded-lg px-4 py-2 transition-colors">
              تجديد الاشتراك
            </button>
          </div>
        )}
        <ErrorBoundary>
        <Tabs value={tab} onValueChange={setTab} dir="rtl">
          <div className="sticky top-16 sm:top-20 z-10 bg-slate-50/95 backdrop-blur-sm py-2 mb-4" data-testid="tabs-sticky-bar">
          <div className="w-full flex flex-wrap justify-start gap-1 bg-white border p-1.5 rounded-xl shadow-sm"
            data-testid="main-tabs">
            {MAIN_TABS.map(({ id, label, icon: Icon, testId }) => {
              const active = id === "group" ? inGroup : id === "settingsgroup" ? inSettings : id === "portalgate" ? inPortal : tab === id;
              return (
                <button key={id} onClick={() => openMain(id)} data-testid={testId}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm transition-colors ${active ? "bg-emerald-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              );
            })}
          </div>
          {inGroup && (
            <div className="w-full flex flex-wrap justify-start gap-1 bg-emerald-50 border border-emerald-100 p-1.5 rounded-xl mt-2"
              data-testid="attendance-subtabs">
              {GROUP_CHILDREN.map((c) => {
                const active = tab === c.id;
                return (
                  <button key={c.id} onClick={() => openChild(c)} data-testid={c.testId}
                    className={`rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${active ? "bg-emerald-700 text-white" : "text-emerald-900 hover:bg-emerald-100"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
          {inSettings && (
            <div className="w-full flex flex-wrap justify-start gap-1 bg-emerald-50 border border-emerald-100 p-1.5 rounded-xl mt-2"
              data-testid="settings-subtabs">
              {SETTINGS_CHILDREN.map((c) => {
                const active = tab === c.id;
                return (
                  <button key={c.id} onClick={() => openSettingsChild(c)} data-testid={c.testId}
                    className={`rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${active ? "bg-emerald-700 text-white" : "text-emerald-900 hover:bg-emerald-100"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
          {inPortal && (
            <div className="w-full flex flex-wrap justify-start gap-1 bg-emerald-50 border border-emerald-100 p-1.5 rounded-xl mt-2"
              data-testid="portal-subtabs">
              {PORTAL_CHILDREN.map((c) => {
                const active = tab === c.id;
                return (
                  <button key={c.id} onClick={() => openPortalChild(c)} data-testid={c.testId}
                    className={`rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${active ? "bg-emerald-700 text-white" : "text-emerald-900 hover:bg-emerald-100"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
          </div>
          <TabsContent value="overview">{gate(<OverviewTab />)}</TabsContent>
          <TabsContent value="attendance">{gate(<AttendanceTab />)}</TabsContent>
          <TabsContent value="lateness">{gate(<LatenessTab />)}</TabsContent>
          <TabsContent value="accountability">{gate(<AccountabilityTab />)}</TabsContent>
          <TabsContent value="deductions">{gate(<DeductionsTab />)}</TabsContent>
          <TabsContent value="performance">
            {gate(<PlanGate feature="performance" onUpgrade={() => setTab("subscription")}><PerformanceTab /></PlanGate>)}
          </TabsContent>
          <TabsContent value="employees">{gate(<EmployeesTab />)}</TabsContent>
          <TabsContent value="portalgate">
            {gate(<PlanGate feature="portal" onUpgrade={() => setTab("subscription")}><StaffPortalGateTab /></PlanGate>)}
          </TabsContent>
          <TabsContent value="settings-timing">{gate(<SettingsTab section="timing" />)}</TabsContent>
          <TabsContent value="settings-inquiries">{gate(<SettingsTab section="inquiries" />)}</TabsContent>
          <TabsContent value="settings-holidays">{gate(<SettingsTab section="holidays" />)}</TabsContent>
          <TabsContent value="settings-branding">{gate(<SettingsTab section="branding" />)}</TabsContent>
          <TabsContent value="extension">{gate(<ExtensionTab />)}</TabsContent>
          <TabsContent value="support"><SupportTab /></TabsContent>
          <TabsContent value="subscription"><MySubscriptionTab /></TabsContent>
          <TabsContent value="portal">
            {gate(<PlanGate feature="circulars" onUpgrade={() => setTab("subscription")}><StaffPortalTab /></PlanGate>)}
          </TabsContent>
          <TabsContent value="permissions">
            {gate(<PlanGate feature="permissions" onUpgrade={() => setTab("subscription")}><PermissionsTab /></PlanGate>)}
          </TabsContent>
          <TabsContent value="audit">
            {gate(<PlanGate feature="audit" onUpgrade={() => setTab("subscription")}><AuditLogTab /></PlanGate>)}
          </TabsContent>
          {user?.role === "admin" && <TabsContent value="admin"><AdminTab /></TabsContent>}
        </Tabs>
        </ErrorBoundary>
      </main>
    </div>
  );
}
